/**
 * AnnotationTimelineItem Component
 * 
 * Specialized memoized timeline item for annotations.
 * Handles all rendering logic internally to enable proper memoization.
 */
import React, { memo, useCallback, useState, useMemo } from 'react';
import { View, Flex, IconButton, Checkbox } from '@smarterservices/smarter-ui';
import { formatTime } from '../../../helpers/utilities';
import CONSTANTS from '../../../config/constants';
import AnnotationTitle from './AnnotationTitle';
import LineDetail from './LineDetail';

const AnnotationTimelineItem = memo(({
    annotation,
    index,
    totalItems,
    isHighlighted,
    baseId,
    bulkEdit,
    isSelected,
    showReviewerTools,
    showDetails,
    PlayerStore,
    onBulkSelectionChange,
    onHeightChange,
}) => {
    // State to track if this annotation's details editor is active
    const [isEditingDetails, setIsEditingDetails] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [showDetailsEditor, setShowDetailsEditor] = useState(false);

    // Check if annotation has existing details (memoized)
    const hasDetails = useMemo(() =>
        annotation?.details && annotation.details.trim() !== '',
        [annotation?.details]
    );
    // Memoize the jump handler
    const handleJumpTo = useCallback(() => {
        PlayerStore.jumpTo(
            annotation.start > CONSTANTS.ANNOTATIONS.SECONDS_LEADING_TO_ANNOTATION
                ? annotation.start - CONSTANTS.ANNOTATIONS.SECONDS_LEADING_TO_ANNOTATION
                : 0
        );
    }, [annotation.start, PlayerStore]);

    // Memoize the selection handler
    const handleSelectionChange = useCallback(() => {
        onBulkSelectionChange(annotation.sid);
    }, [annotation.sid, onBulkSelectionChange]);

    // Handler to toggle details editing mode
    const handleToggleDetailsEdit = useCallback(() => {
        setIsEditingDetails(prev => !prev);
        if (onHeightChange) {
            setTimeout(() => onHeightChange(), 0);
        }
    }, [onHeightChange]);

    // Calculate severity (memoized)
    const severity = useMemo(() =>
        PlayerStore.flagScoreToSeverity(annotation.score),
        [PlayerStore, annotation.score]
    );

    const themeOverride = useMemo(() => ({
        [`${severity.color}Background`]: severity.backgroundColor,
        [`${severity.color}Color`]: severity.iconColor
    }), [severity.color, severity.backgroundColor, severity.iconColor]);

    const figureMargin = useMemo(() => {
        if (index === 0) return '15px 0 0 0';
        if (index === totalItems - 1) return '0 0 5px 0';
        return undefined;
    }, [index, totalItems]);

    const figureHeight = useMemo(() => {
        if (index !== totalItems - 1) return '105%';
        if (index === totalItems - 1 && showDetails) return '105%';
        return '10px';
    }, [index, totalItems, showDetails]);

    const handleMouseEnter = useCallback(() => {
        setIsHovered(true);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setIsHovered(false);
    }, []);

    return (
        <View
            as="div"
            key={`annotation_item_wrapper_${annotation.sid}`}
            id={`annotation_item_wrapper_${annotation.sid}`}
            background={isHighlighted ? "secondary" : "transparent"}
            borderRadius="medium"
            borderWidth="small"
            borderColor={isHighlighted ? "primary" : "transparent"}
            data-time={annotation.start}
            style={{ transition: 'all 0.3s ease-in-out', position: 'relative' }}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <Flex as="div" className="timeline-item" alignItems="stretch" style={{ height: '100%' }} padding="none none none small">
                {/* Label with time and checkbox */}
                <Flex.Item as="div" className="timeline-item-label" align="start" style={{ height: '100%' }}>
                    <View as="div" margin="none none small none">
                        <View as="div" margin="xx-small xx-small none none">
                            <Flex as="div" justifyItems="end" width={bulkEdit ? "80px" : "65px"}>
                                <Flex.Item width="20px">
                                    {bulkEdit && (
                                        <Checkbox
                                            size="small"
                                            checked={isSelected}
                                            value={annotation.sid}
                                            onChange={handleSelectionChange}
                                        />
                                    )}
                                </Flex.Item>
                                <Flex.Item shouldGrow textAlign="end">
                                    {formatTime(annotation.start)}
                                </Flex.Item>
                            </Flex>
                        </View>
                    </View>
                </Flex.Item>

                {/* Icon with vertical line */}
                <Flex.Item as="div" position="relative" style={{ height: '100%' }}>
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: '50%',
                            width: '1px',
                            height: figureHeight,
                            backgroundColor: '#ccc',
                            margin: figureMargin,
                        }}
                    />
                    <View as="div" margin="none none small none" padding="xxx-small none none none">
                        <IconButton
                            as="button"
                            cursor="pointer"
                            interaction="enabled"
                            shape="circle"
                            screenReaderLabel="Jump to annotation start"
                            margin="xxx-small"
                            color={severity.color}
                            size="small"
                            className='timeline-icon-button'
                            onClick={handleJumpTo}
                            themeOverride={themeOverride}
                        >
                            {severity.icon}
                        </IconButton>
                    </View>
                </Flex.Item>

                {/* Content */}
                <Flex.Item position="relative" height="100%" as="div" shouldGrow shouldShrink align="start">
                    <View as="div" className="timeline-item-content" margin="none none small none">
                        <View as="div" margin="xx-small none none x-small">
                            <AnnotationTitle
                                annotation={annotation}
                                onToggleDetailsEdit={handleToggleDetailsEdit}
                                isEditingDetails={isEditingDetails}
                                isHovered={showReviewerTools && isHovered}
                            />
                        </View>
                        <View as="div" margin="none none none x-small">
                            <LineDetail
                                annotation={annotation}
                                reviewerTools={showReviewerTools && isHovered}
                                details={(showDetails && hasDetails) || isEditingDetails}
                                showDetailsEditor={showDetailsEditor}
                                onToggleDetailsEditor={() => {
                                    setShowDetailsEditor(prev => !prev);
                                    if (onHeightChange) {
                                        setTimeout(() => onHeightChange(), 0);
                                    }
                                }}
                                hasDetails={hasDetails}
                                onHeightChange={onHeightChange}
                            />
                        </View>
                    </View>
                </Flex.Item>
            </Flex>
        </View>
    );
}, (prevProps, nextProps) => {
    // Only re-render if these specific props change
    // Return true if props are equal (skip re-render), false if different (do re-render)
    return (
        prevProps.annotation.sid === nextProps.annotation.sid &&
        prevProps.isHighlighted === nextProps.isHighlighted &&
        prevProps.bulkEdit === nextProps.bulkEdit &&
        prevProps.isSelected === nextProps.isSelected &&
        prevProps.showReviewerTools === nextProps.showReviewerTools &&
        prevProps.showDetails === nextProps.showDetails &&
        prevProps.annotation.score === nextProps.annotation.score &&
        prevProps.annotation.start === nextProps.annotation.start &&
        prevProps.annotation?.range?.end === nextProps.annotation?.range?.end &&
        prevProps.annotation?.reviewedDate === nextProps.annotation?.reviewedDate &&
        prevProps.annotation?.details === nextProps.annotation?.details &&
        prevProps.index === nextProps.index &&
        prevProps.totalItems === nextProps.totalItems
    );
});

AnnotationTimelineItem.displayName = 'AnnotationTimelineItem';

export default AnnotationTimelineItem;
